import { Proposal } from "generated/graphql";
import useCreateProposalWorksheetItem from "hooks/proposals/useCreateProposalWorksheetItem";
import useCustomAddProductsToProposalStage from "hooks/proposals/useCustomAddProductsToProposalStage";
import { ProposalStage } from "generated/graphql";
import SelectProductForm, { ProductMeasurement } from "modules/products/forms/SelectProductForm";
import { useCallback } from "react";

type AddProductDrawerActionsProps = {
  stage: ProposalStage;
  onSuccess: (proposal: Proposal) => void;
  onClose: () => void;
};

export default function useAddProductDrawerActions({
  stage,
  onSuccess,
  onClose,
}: AddProductDrawerActionsProps) {
  const [addProductsToProposalStage, { loading, isSuccess, getData }] =
    useCustomAddProductsToProposalStage();
  const [
    createProposalWorksheetItem,
    {
      loading: creatingWorksheetItem,
      isSuccess: isWorksheetItemSuccess,
      getData: getWorksheetItemData,
    },
  ] = useCreateProposalWorksheetItem();

  const handleOnSubmit = useCallback(
    async (measurements: ProductMeasurement[]) => {
      const result = await addProductsToProposalStage({
        variables: {
          input: {
            proposalStageId: stage.id,
            products: measurements.map((measurement) => ({
              id: measurement.id,
              jobSize: parseInt(measurement.jobSize.toString()) || 0,
              dailyUnitsComplete: measurement.dailyUnitsComplete || null,
              jobDuration: parseInt(measurement.jobDuration) || null,
              ...(measurement.mapData && { mapData: measurement.mapData }),
            })),
          },
        },
      });

      const success = isSuccess(result.data);
      const data = getData(result);

      if (success) {
        onSuccess(data as Proposal);
        onClose();
      }

      return { success, data };
    },
    [stage.id, addProductsToProposalStage, isSuccess, getData, onSuccess, onClose]
  );

  const insertWorksheetItem = useCallback(
    async (worksheetProductId?: string) => {
      const result = await createProposalWorksheetItem({
        variables: {
          proposalStageId: stage.id,
          worksheetProductId: worksheetProductId ?? null,
        },
      });

      if (isWorksheetItemSuccess(result.data)) {
        onSuccess(getWorksheetItemData(result) as Proposal);
        onClose();
      }
    },
    [
      stage.id,
      createProposalWorksheetItem,
      isWorksheetItemSuccess,
      getWorksheetItemData,
      onSuccess,
      onClose,
    ]
  );

  return {
    handleOnSubmit,
    insertWorksheetItem,
    loading,
    creatingWorksheetItem,
  };
}
